import { A } from '@solidjs/router';
import { type Component, createResource, createSignal, For, Show } from 'solid-js';
import {
  type CollaboratorStatus,
  fetchJoinedProjects,
  type JoinedProject,
  leaveProject,
} from '../services/api';

/**
 * JoinedProjects Page
 *
 * Shows projects that the user has joined/is collaborating on.
 * Users can view pending requests, approved collaborations, and leave projects.
 */
const JoinedProjects: Component = () => {
  const [statusFilter, setStatusFilter] = createSignal<CollaboratorStatus>('approved');
  const [page, setPage] = createSignal(0);
  const limit = 12;

  const [projects, { refetch }] = createResource(
    () => ({ offset: page() * limit, limit, status: statusFilter() }),
    fetchJoinedProjects,
  );

  const [leavingId, setLeavingId] = createSignal<string | null>(null);

  const handleLeaveProject = async (projectId: string) => {
    if (!confirm('Are you sure you want to leave this project?')) {
      return;
    }
    setLeavingId(projectId);
    try {
      await leaveProject(projectId);
      refetch();
    } catch (error) {
      console.error('Failed to leave project:', error);
    } finally {
      setLeavingId(null);
    }
  };

  const totalPages = () => Math.ceil((projects()?.total || 0) / limit);

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-purple-500/20 text-purple-400';
      case 'contributor':
        return 'bg-blue-500/20 text-blue-400';
      case 'viewer':
        return 'bg-gray-500/20 text-gray-400';
      default:
        return 'bg-gray-500/20 text-gray-400';
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-500/20 text-green-400';
      case 'pending':
        return 'bg-yellow-500/20 text-yellow-400';
      case 'rejected':
        return 'bg-red-500/20 text-red-400';
      default:
        return 'bg-gray-500/20 text-gray-400';
    }
  };

  return (
    <div>
      {/* Page Header */}
      <div class="mb-6">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-2xl font-semibold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
              Joined Projects
            </h1>
            <p class="text-gray-400 text-sm mt-1">
              Projects you're collaborating on with other users
            </p>
          </div>
          <A
            href="/marketplace"
            class="inline-flex items-center px-4 py-2 rounded-lg bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white font-medium transition-all"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="mr-2"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            Find Projects
          </A>
        </div>
      </div>

      {/* Status Filter */}
      <div class="mb-6 flex gap-2">
        <button
          onClick={() => {
            setStatusFilter('approved');
            setPage(0);
          }}
          class={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            statusFilter() === 'approved'
              ? 'bg-green-500/20 text-green-400 border border-green-500/30'
              : 'bg-gray-800 text-gray-400 border border-gray-700 hover:border-gray-600'
          }`}
        >
          Active
        </button>
        <button
          onClick={() => {
            setStatusFilter('pending');
            setPage(0);
          }}
          class={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            statusFilter() === 'pending'
              ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
              : 'bg-gray-800 text-gray-400 border border-gray-700 hover:border-gray-600'
          }`}
        >
          Pending
        </button>
        <button
          onClick={() => {
            setStatusFilter('rejected');
            setPage(0);
          }}
          class={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            statusFilter() === 'rejected'
              ? 'bg-red-500/20 text-red-400 border border-red-500/30'
              : 'bg-gray-800 text-gray-400 border border-gray-700 hover:border-gray-600'
          }`}
        >
          Rejected
        </button>
      </div>

      {/* Loading State */}
      <Show when={projects.loading}>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <For each={[1, 2, 3, 4, 5, 6]}>
            {() => (
              <div class="bg-gray-800/50 rounded-lg border border-gray-700 p-6 animate-pulse">
                <div class="h-6 bg-gray-700 rounded w-3/4 mb-4" />
                <div class="h-4 bg-gray-700 rounded w-full mb-2" />
                <div class="h-4 bg-gray-700 rounded w-2/3" />
              </div>
            )}
          </For>
        </div>
      </Show>

      {/* Error State */}
      <Show when={projects.error}>
        <div class="bg-red-900/20 border border-red-700 rounded-lg p-6 text-center">
          <p class="text-red-400">Failed to load projects. Please try again.</p>
          <button
            onClick={() => refetch()}
            class="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white"
          >
            Retry
          </button>
        </div>
      </Show>

      {/* Empty State */}
      <Show when={!projects.loading && !projects.error && projects()?.items.length === 0}>
        <div class="bg-gray-800/50 rounded-lg border border-gray-700 p-12 text-center">
          <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-500/10 mb-6">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="text-blue-400"
            >
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </div>
          <h2 class="text-xl font-medium text-gray-200 mb-2">
            No{' '}
            {statusFilter() === 'approved'
              ? 'Active'
              : statusFilter() === 'pending'
                ? 'Pending'
                : 'Rejected'}{' '}
            Collaborations
          </h2>
          <p class="text-gray-400 mb-6 max-w-md mx-auto">
            <Show when={statusFilter() === 'approved'}>
              You haven't joined any projects yet. Find interesting projects in the marketplace and
              request to collaborate.
            </Show>
            <Show when={statusFilter() === 'pending'}>
              You don't have any pending join requests. Browse the marketplace to find projects to
              collaborate on.
            </Show>
            <Show when={statusFilter() === 'rejected'}>
              No rejected requests. Your collaboration requests are either pending or approved.
            </Show>
          </p>
          <A
            href="/marketplace"
            class="inline-flex items-center px-4 py-2 rounded-lg bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white font-medium transition-all"
          >
            Browse Marketplace
          </A>
        </div>
      </Show>

      {/* Projects Grid */}
      <Show when={!projects.loading && !projects.error && (projects()?.items.length || 0) > 0}>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <For each={projects()?.items}>
            {(project: JoinedProject) => (
              <div class="bg-gray-800/50 rounded-lg border border-gray-700 hover:border-gray-600 transition-colors overflow-hidden">
                <div class="p-6">
                  <div class="flex items-start justify-between mb-3">
                    <h3 class="font-medium text-gray-200 truncate flex-1">{project.name}</h3>
                    <Show when={project.collaboratorStatus === 'approved'}>
                      <button
                        onClick={() => handleLeaveProject(project.id)}
                        disabled={leavingId() === project.id}
                        class="ml-2 p-1 text-gray-400 hover:text-red-400 transition-colors disabled:opacity-50"
                        title="Leave project"
                      >
                        <Show
                          when={leavingId() !== project.id}
                          fallback={
                            <svg
                              class="animate-spin h-5 w-5"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                class="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                stroke-width="4"
                              />
                              <path
                                class="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              />
                            </svg>
                          }
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          >
                            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                            <polyline points="16 17 21 12 16 7" />
                            <line x1="21" x2="9" y1="12" y2="12" />
                          </svg>
                        </Show>
                      </button>
                    </Show>
                  </div>
                  <p class="text-sm text-gray-400 line-clamp-2 mb-4">
                    {project.goal || project.description || 'No description'}
                  </p>
                  <div class="flex items-center gap-2 text-xs mb-4">
                    <span class={`px-2 py-1 rounded ${getRoleBadgeColor(project.role)}`}>
                      {project.role}
                    </span>
                    <span
                      class={`px-2 py-1 rounded ${getStatusBadgeColor(project.collaboratorStatus)}`}
                    >
                      {project.collaboratorStatus}
                    </span>
                  </div>
                  <Show when={project.requestMessage && project.collaboratorStatus === 'pending'}>
                    <p class="text-xs text-gray-500 italic mb-4 line-clamp-2">
                      "{project.requestMessage}"
                    </p>
                  </Show>
                  <div class="flex items-center justify-between">
                    <span class="text-xs text-gray-500">
                      Requested {new Date(project.requestedAt).toLocaleDateString()}
                    </span>
                    <Show when={project.collaboratorStatus === 'approved'}>
                      <A
                        href={`/projects/${project.id}`}
                        class="text-sm text-purple-400 hover:text-purple-300 transition-colors"
                      >
                        Open Project
                      </A>
                    </Show>
                  </div>
                </div>
              </div>
            )}
          </For>
        </div>

        {/* Pagination */}
        <Show when={totalPages() > 1}>
          <div class="flex items-center justify-center gap-2 mt-8">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page() === 0}
              class="px-3 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span class="text-gray-400 px-4">
              Page {page() + 1} of {totalPages()}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages() - 1, p + 1))}
              disabled={page() >= totalPages() - 1}
              class="px-3 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </Show>
      </Show>

      {/* Feature Info */}
      <div class="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
        <div class="bg-gray-800/30 rounded-lg border border-gray-700/50 p-6">
          <div class="text-purple-400 mb-3">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </div>
          <h3 class="font-medium text-gray-200 mb-2">Collaborate</h3>
          <p class="text-sm text-gray-400">
            Work together with project owners and other contributors on shared goals.
          </p>
        </div>

        <div class="bg-gray-800/30 rounded-lg border border-gray-700/50 p-6">
          <div class="text-blue-400 mb-3">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <rect width="18" height="18" x="3" y="3" rx="2" />
              <path d="M7 7h10" />
              <path d="M7 12h10" />
              <path d="M7 17h10" />
            </svg>
          </div>
          <h3 class="font-medium text-gray-200 mb-2">Role-Based Access</h3>
          <p class="text-sm text-gray-400">
            Viewers can see, contributors can edit, and admins can manage the project.
          </p>
        </div>

        <div class="bg-gray-800/30 rounded-lg border border-gray-700/50 p-6">
          <div class="text-green-400 mb-3">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" />
              <path d="m9 12 2 2 4-4" />
            </svg>
          </div>
          <h3 class="font-medium text-gray-200 mb-2">Owner Approval</h3>
          <p class="text-sm text-gray-400">
            Project owners review and approve collaboration requests to maintain quality.
          </p>
        </div>
      </div>
    </div>
  );
};

export default JoinedProjects;
